<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Catálogo de Servicios
          <span class="px-2 sm:px-3 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs sm:text-sm font-black rounded-lg border border-blue-200 dark:border-blue-800">
            {{ servicios.length }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Gestiona las reparaciones y servicios técnicos de tu negocio</p>
      </div>
      
      <button @click="abrirModalCrear" class="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-[10px] sm:text-sm tracking-widest transition-all shadow-lg shadow-blue-200 dark:shadow-none flex items-center gap-2 w-full sm:w-auto justify-center">
        <Icon icon="mdi:plus-circle" class="text-base sm:text-xl" />
        <span class="hidden sm:inline">Nuevo Servicio</span>
        <span class="sm:hidden">Nuevo</span>
      </button>
    </div>

    <!-- Analytics & Stats Summary -->
    <div class="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-5 text-white">
      <div class="p-2.5 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-500/90 to-indigo-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex flex-col sm:flex-row items-center gap-1 sm:gap-3">
          <div class="p-1.5 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
            <Icon icon="mdi:wrench" class="text-base sm:text-2xl" />
          </div>
          <div class="text-center sm:text-left">
            <p class="text-blue-100 text-[8px] sm:text-xs font-semibold uppercase tracking-wider">Total</p>
            <p class="text-lg sm:text-3xl font-bold">{{ servicios.length }}</p>
          </div>
        </div>
      </div>
      <div class="p-2.5 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-500/90 to-teal-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex flex-col sm:flex-row items-center gap-1 sm:gap-3">
          <div class="p-1.5 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
            <Icon icon="mdi:check-circle" class="text-base sm:text-2xl" />
          </div>
          <div class="text-center sm:text-left">
            <p class="text-emerald-100 text-[8px] sm:text-xs font-semibold uppercase tracking-wider">Activos</p>
            <p class="text-lg sm:text-3xl font-bold">{{ servicios.filter(s => s.estado === 'activo').length }}</p>
          </div>
        </div>
      </div>
      <div class="p-2.5 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-amber-500/90 to-orange-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex flex-col sm:flex-row items-center gap-1 sm:gap-3">
          <div class="p-1.5 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
            <Icon icon="mdi:cash-multiple" class="text-base sm:text-2xl" />
          </div>
          <div class="text-center sm:text-left">
            <p class="text-amber-100 text-[8px] sm:text-xs font-semibold uppercase tracking-wider">Promedio</p>
            <p class="text-sm sm:text-2xl font-bold">{{ formatearPrecio(promedioPrecio, 'usd') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters & Search Card -->
    <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-3 sm:p-6 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 items-end">
        <div class="lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Buscar Servicio</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-xl group-focus-within:text-blue-500 transition-colors" />
            <input v-model="filtros.busqueda" type="text" placeholder="Ingresa código o nombre del servicio..." class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base" @input="paginaActual = 1" />
          </div>
        </div>
        
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select v-model="filtros.estado" @change="cargarServicios" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>

        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Ordenar Por</label>
          <select v-model="filtros.orden" @change="paginaActual = 1" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="nombre">Nombre (A-Z)</option>
            <option value="nombre_desc">Nombre (Z-A)</option>
            <option value="precioUsd_desc">Precio (Mayor a Menor)</option>
            <option value="precioUsd">Precio (Menor a Mayor)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Modern Data Display (Table Card) -->
    <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-fadeInUp delay-100">
      <!-- Versión Desktop: Tabla -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50/50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 text-[10px] sm:text-xs font-black uppercase tracking-widest border-b border-gray-100 dark:border-gray-800">
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-left">Identificador</th>
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-left">Detalles del Servicio</th>
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-right">Valorización</th>
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-center">Duración</th>
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-center">Estado</th>
              <th class="px-5 sm:px-6 py-4 sm:py-5 text-center">Gestión</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800/50">
            <tr v-if="cargando">
              <td colspan="6" class="px-5 sm:px-6 py-16 sm:py-20 text-center">
                <div class="flex flex-col items-center gap-3 sm:gap-4">
                  <Icon icon="mdi:loading" class="text-4xl sm:text-5xl text-blue-500 animate-spin" />
                  <p class="text-gray-400 font-medium text-sm animate-pulse">Sincronizando con el servidor...</p>
                </div>
              </td>
            </tr>
            
            <tr v-else-if="serviciosFiltrados.length === 0">
              <td colspan="6" class="px-5 sm:px-6 py-16 sm:py-20 text-center">
                <div class="flex flex-col items-center gap-3 sm:gap-4 text-gray-400">
                  <Icon icon="mdi:database-off-outline" class="text-5xl sm:text-6xl opacity-20" />
                  <p class="text-base sm:text-lg font-medium">No se encontraron servicios técnicos</p>
                  <button @click="filtros.busqueda = ''; filtros.estado = 'all';" class="text-blue-500 underline text-xs sm:text-sm">Limpiar filtros</button>
                </div>
              </td>
            </tr>

            <tr v-else v-for="servicio in serviciosPaginados" :key="servicio.idServicio" class="hover:bg-blue-50/30 dark:hover:bg-blue-500/5 transition-colors group/row">
              <td class="px-5 sm:px-6 py-4 sm:py-5">
                <span class="px-2.5 sm:px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg text-[10px] sm:text-xs font-bold tracking-tight border border-gray-200 dark:border-gray-700">
                  {{ servicio.codigo }}
                </span>
              </td>
              <td class="px-5 sm:px-6 py-4 sm:py-5">
                <div class="flex flex-col">
                  <span class="text-gray-800 dark:text-gray-200 font-bold text-sm sm:text-base group-hover/row:text-blue-600 dark:group-hover/row:text-blue-400 transition-colors">
                    {{ servicio.nombre }}
                  </span>
                  <span class="text-gray-400 dark:text-gray-500 text-[10px] sm:text-xs line-clamp-1 max-w-[150px] sm:max-w-[250px]">
                    {{ servicio.descripcion || 'Sin descripción adicional' }}
                  </span>
                </div>
              </td>
              <td class="px-5 sm:px-6 py-4 sm:py-5 text-right">
                <div class="flex flex-col">
                  <span class="text-indigo-600 dark:text-indigo-400 font-black text-base sm:text-lg">
                    {{ formatearPrecio(servicio.precioUsd, 'usd') }}
                  </span>
                  <span class="text-gray-400 dark:text-gray-500 text-[8px] sm:text-[10px] font-bold uppercase tracking-tighter">
                    {{ formatearPrecio(servicio.precioBs, 'bs') }}
                  </span>
                </div>
              </td>
              <td class="px-5 sm:px-6 py-4 sm:py-5 text-center">
                <div class="flex items-center justify-center gap-1 sm:gap-2 text-gray-500 dark:text-gray-400">
                  <Icon icon="mdi:clock-outline" class="text-base sm:text-lg opacity-60" />
                  <span class="text-xs sm:text-sm font-semibold tracking-tight">{{ formatearDuracion(servicio.duracion) }}</span>
                </div>
              </td>
              <td class="px-5 sm:px-6 py-4 sm:py-5 text-center">
                <button @click="toggleEstado(servicio)" :class="['relative inline-flex items-center px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-full text-[8px] sm:text-[10px] font-black uppercase tracking-widest transition-all duration-300 active:scale-95', servicio.estado === 'activo' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500 hover:text-white' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 hover:bg-rose-500 hover:text-white']">
                  {{ servicio.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </button>
              </td>
              <td class="px-5 sm:px-6 py-4 sm:py-5">
                <div class="flex justify-center items-center gap-1 sm:gap-3">
                  <button @click="abrirModalEditar(servicio)" class="p-1.5 sm:p-2.5 rounded-xl bg-blue-500/5 text-blue-500 hover:bg-blue-500 hover:text-white transition-all duration-300" title="Editar servicio">
                    <Icon icon="mdi:pencil-outline" class="text-base sm:text-xl" />
                  </button>
                  <button @click="confirmarEliminar(servicio)" class="p-1.5 sm:p-2.5 rounded-xl bg-rose-500/5 text-rose-600 hover:bg-rose-500 hover:text-white transition-all duration-300" title="Eliminar servicio">
                    <Icon icon="mdi:trash-can-outline" class="text-base sm:text-xl" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2 sm:p-3">
        <div v-if="cargando" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!cargando && serviciosFiltrados.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:database-off-outline" class="text-5xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay servicios</h4>
          </div>
        </div>
        
        <div v-for="servicio in serviciosPaginados" :key="servicio.idServicio" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-[10px] font-bold border border-gray-200 dark:border-gray-600">
                {{ servicio.codigo }}
              </span>
              <button @click="toggleEstado(servicio)" :class="['px-2 py-0.5 rounded-full text-[7px] sm:text-[8px] font-black uppercase border transition-all', servicio.estado === 'activo' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20']">
                {{ servicio.estado === 'activo' ? 'Activo' : 'Inactivo' }}
              </button>
            </div>
            <div class="flex items-center gap-1">
              <span class="text-indigo-600 dark:text-indigo-400 font-black text-base">${{ (servicio.precioUsd || 0).toFixed(2) }}</span>
            </div>
          </div>

          <div class="mb-2">
            <p class="font-black text-gray-800 dark:text-white text-sm">{{ servicio.nombre }}</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ servicio.descripcion || 'Sin descripción' }}</p>
          </div>

          <div class="flex flex-wrap items-center gap-3 mb-3 text-[10px] text-gray-500 dark:text-gray-400">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:clock-outline" class="text-sm" />
              <span class="font-bold">{{ formatearDuracion(servicio.duracion) }}</span>
            </div>
            <div class="flex items-center gap-1">
              <Icon icon="mdi:shield-check" class="text-sm" />
              <span class="font-bold">{{ servicio.garantiaDias || 0 }} días</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="abrirModalEditar(servicio)" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm" title="Editar">
              <Icon icon="mdi:pencil-outline" class="text-sm" />
            </button>
            <button @click="confirmarEliminar(servicio)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Eliminar">
              <Icon icon="mdi:trash-can-outline" class="text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Footer Paginación -->
      <div v-if="totalPaginas > 1" class="flex flex-col sm:flex-row justify-between items-center px-3 sm:px-8 py-3 sm:py-6 bg-gray-50/50 dark:bg-gray-800/30 gap-3 sm:gap-4">
        <p class="text-[9px] sm:text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
          Página <span class="text-blue-500">{{ paginaActual }}</span> de <span class="text-gray-600 dark:text-gray-300">{{ totalPaginas }}</span>
        </p>
        <div class="flex items-center gap-1 sm:gap-2">
          <button @click="paginaActual--" :disabled="paginaActual === 1" class="p-2 sm:p-3 bg-white dark:bg-gray-800 border-none rounded-xl sm:rounded-2xl shadow-sm text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95">
            <Icon icon="mdi:chevron-left" class="text-xl sm:text-2xl" />
          </button>
          <div class="flex gap-0.5 sm:gap-1">
            <template v-for="n in totalPaginas" :key="n">
              <button v-if="n === 1 || n === totalPaginas || (n >= paginaActual - 1 && n <= paginaActual + 1)" @click="paginaActual = n" :class="['w-8 sm:w-10 h-8 sm:h-10 rounded-xl sm:rounded-2xl text-[10px] sm:text-sm font-bold transition-all', paginaActual === n ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-white dark:bg-gray-800 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400']">
                {{ n }}
              </button>
              <span v-else-if="n === 2 || n === totalPaginas - 1" class="text-gray-300 px-0.5 sm:px-1 pt-1 sm:pt-2 font-black text-[10px] sm:text-sm">...</span>
            </template>
          </div>
          <button @click="paginaActual++" :disabled="paginaActual === totalPaginas" class="p-2 sm:p-3 bg-white dark:bg-gray-800 border-none rounded-xl sm:rounded-2xl shadow-sm text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95">
            <Icon icon="mdi:chevron-right" class="text-xl sm:text-2xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR -->
    <Teleport to="body">
      <div v-if="modalVisible" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9998]" @click.self="cerrarModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-zoomIn max-h-[95vh] flex flex-col">
          <!-- Header -->
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon :icon="editando ? 'mdi:pencil' : 'mdi:plus'" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">{{ editando ? 'Modificar Registro' : 'Nuevo Servicio Técnico' }}</h3>
                <p class="text-[8px] sm:text-[10px] text-blue-100 font-bold">{{ editando ? 'Actualiza la información del servicio' : 'Registra un nuevo servicio al catálogo' }}</p>
              </div>
            </div>
            <button @click="cerrarModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <!-- Body -->
          <div class="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <form @submit.prevent="guardarServicio" class="space-y-4 sm:space-y-6">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Código *</label>
                  <div class="relative flex items-center">
                    <Icon icon="mdi:barcode" class="absolute left-3 sm:left-4 text-gray-400 text-sm sm:text-base" />
                    <input v-model="form.codigo" type="text" required placeholder="Ej: REP-001" class="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-bold dark:text-white outline-none transition-all" />
                  </div>
                </div>

                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Nombre *</label>
                  <div class="relative flex items-center">
                    <Icon icon="mdi:label-outline" class="absolute left-3 sm:left-4 text-gray-400 text-sm sm:text-base" />
                    <input v-model="form.nombre" type="text" required placeholder="Cambio de Pantalla..." class="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-bold dark:text-white outline-none transition-all" @blur="form.nombre = toTitleCase(form.nombre)" />
                  </div>
                </div>

                <div class="col-span-full space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Descripción</label>
                  <textarea v-model="form.descripcion" rows="2" placeholder="Describe brevemente el alcance del servicio..." class="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-bold dark:text-white outline-none transition-all resize-none" @blur="form.descripcion = toTitleCase(form.descripcion)"></textarea>
                </div>
                
                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Precio (USD) *</label>
                  <div class="relative group flex items-center">
                    <span class="absolute left-3 sm:left-4 font-bold text-gray-400 group-focus-within:text-blue-500 text-xs sm:text-sm">$</span>
                    <input v-model.number="form.precioUsd" type="number" min="0" step="0.01" required @input="calcularPrecioBs" class="w-full pl-7 sm:pl-9 pr-3 sm:pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-black dark:text-white outline-none transition-all" />
                  </div>
                </div>
                
                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Referencia (BS)</label>
                  <div class="flex gap-1 sm:gap-2">
                    <div class="relative flex-1 flex items-center">
                      <span class="absolute left-3 sm:left-4 font-bold text-gray-400 text-xs sm:text-sm">Bs</span>
                      <input v-model.number="form.precioBs" type="number" min="0" step="0.01" :readonly="usarTasaAutomatica" :class="['w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-black dark:text-white outline-none transition-all', usarTasaAutomatica ? 'bg-gray-100 dark:bg-gray-800/50' : '']" />
                    </div>
                    <button type="button" @click="toggleModoPrecio" :class="['px-2 sm:px-4 rounded-xl font-black text-[8px] sm:text-[10px] uppercase tracking-wider transition-all shadow-sm active:scale-90', usarTasaAutomatica ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400']">
                      {{ usarTasaAutomatica ? 'Auto' : 'Manual' }}
                    </button>
                  </div>
                </div>

                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Garantía (Días)</label>
                  <input v-model.number="form.garantiaDias" type="number" min="0" class="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs sm:text-sm font-black text-amber-600 dark:text-amber-400 dark:text-white outline-none transition-all" />
                </div>

                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Duración Estimada</label>
                  <div class="flex items-center gap-2 sm:gap-3 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl border-2 border-gray-200 dark:border-gray-600">
                    <Icon icon="mdi:timer-sand" class="text-xl sm:text-2xl text-gray-400" />
                    <input v-model.number="form.duracion" type="range" min="0" max="480" step="15" class="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600 transition-all" />
                    <span class="min-w-[50px] sm:min-w-[70px] text-center font-bold text-gray-800 dark:text-white text-[10px] sm:text-sm">{{ formatearDuracion(form.duracion) }}</span>
                  </div>
                </div>

                <div class="space-y-1 sm:space-y-2">
                  <label class="text-[9px] sm:text-[11px] font-black text-gray-400 tracking-widest uppercase ml-1">Estatus</label>
                  <div class="flex p-1 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-600">
                    <button type="button" @click="form.estado = 'activo'" :class="['flex-1 py-1.5 sm:py-2 text-[8px] sm:text-[10px] font-black uppercase tracking-widest rounded-lg transition-all', form.estado === 'activo' ? 'bg-white dark:bg-gray-700 text-emerald-500 shadow-sm' : 'text-gray-400']">Activo</button>
                    <button type="button" @click="form.estado = 'inactivo'" :class="['flex-1 py-1.5 sm:py-2 text-[8px] sm:text-[10px] font-black uppercase tracking-widest rounded-lg transition-all', form.estado === 'inactivo' ? 'bg-white dark:bg-gray-700 text-rose-500 shadow-sm' : 'text-gray-400']">Pausado</button>
                  </div>
                </div>
              </div>
              
              <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="cerrarModal" class="w-full sm:flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-[10px] sm:text-xs">Cancelar</button>
                <button type="submit" :disabled="guardando" class="w-full sm:flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-200 dark:shadow-none flex items-center justify-center gap-1 sm:gap-2 text-[10px] sm:text-xs disabled:opacity-50">
                  <Icon v-if="guardando" icon="mdi:loading" class="animate-spin text-base sm:text-lg" />
                  <Icon v-else icon="mdi:content-save" class="text-base sm:text-lg" />
                  {{ guardando ? 'Guardando...' : (editando ? 'Confirmar Cambios' : 'Registrar Servicio') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import { toTitleCase } from '@/utils/stringUtils.js';
import { useModalStore } from "@/store/modal";
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { 
  obtenerServicios, crearServicio, actualizarServicio, eliminarServicio,
  activarServicio, desactivarServicio, verificarServicioPorCodigo,
  formatearPrecio, formatearDuracion
} from '@/api/servicios.js';

export default {
  name: 'ServiciosList',
  components: { 
    Icon
  },
  setup() {
    const toast = useToast();
    const modal = useModalStore();
    return { toast, modal };
  },
  data() {
    return {
      servicios: [],
      cargando: false,
      guardando: false,
      modalVisible: false,
      editando: false,
      usarTasaAutomatica: true,
      tasaBCV: null,
      paginaActual: 1,
      itemsPorPagina: 5,
      maxVisiblePages: 10,
      filtros: {
        busqueda: '',
        estado: 'all',
        orden: 'nombre'
      },
      form: {
        idServicio: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        precioUsd: 0,
        precioBs: 0,
        costoManoObraUsd: 0,
        duracion: 30,
        garantiaDias: 0,
        garantiaDescripcion: '',
        estado: 'activo'
      }
    };
  },
  computed: {
    promedioPrecio() {
      const total = this.servicios.reduce((sum, s) => sum + (s.precioUsd || 0), 0);
      return this.servicios.length > 0 ? total / this.servicios.length : 0;
    },
    serviciosFiltrados() {
      let resultado = [...this.servicios];
      
      if (this.filtros.busqueda) {
        const busqueda = this.filtros.busqueda.toLowerCase();
        resultado = resultado.filter(s => 
          s.codigo?.toLowerCase().includes(busqueda) || 
          s.nombre?.toLowerCase().includes(busqueda) ||
          s.descripcion?.toLowerCase().includes(busqueda)
        );
      }
      
      if (this.filtros.estado === 'activos') {
        resultado = resultado.filter(s => s.estado === 'activo');
      } else if (this.filtros.estado === 'inactivos') {
        resultado = resultado.filter(s => s.estado === 'inactivo');
      }
      
      if (this.filtros.orden === 'nombre') {
        resultado.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      } else if (this.filtros.orden === 'nombre_desc') {
        resultado.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
      } else if (this.filtros.orden === 'precioUsd_desc') {
        resultado.sort((a, b) => (b.precioUsd || 0) - (a.precioUsd || 0));
      } else if (this.filtros.orden === 'precioUsd') {
        resultado.sort((a, b) => (a.precioUsd || 0) - (b.precioUsd || 0));
      }
      
      return resultado;
    },
    serviciosPaginados() {
      const inicio = (this.paginaActual - 1) * this.itemsPorPagina;
      return this.serviciosFiltrados.slice(inicio, inicio + this.itemsPorPagina);
    },
    totalPaginas() {
      return Math.ceil(this.serviciosFiltrados.length / this.itemsPorPagina);
    },
    displayedPages() {
      const total = this.totalPaginas;
      const max = this.maxVisiblePages;
      const current = this.paginaActual;
      
      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;
      
      if (end > total) {
        end = total;
        start = Math.max(1, end - max + 1);
      }
      
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  },
  mounted() {
    this.cargarServicios();
    this.cargarTasaBCV();
  },
  methods: {
    toTitleCase,
    formatearPrecio,
    formatearDuracion,
    
    async cargarTasaBCV() {
      try {
        const res = await obtenerTasaVentaActual();
        if (res.success && res.data) {
          this.tasaBCV = res.data;
          if (this.usarTasaAutomatica && this.form.precioUsd > 0) {
            this.calcularPrecioBs();
          }
        }
      } catch (error) {
        this.usarTasaAutomatica = false;
      }
    },
    
    calcularPrecioBs() {
      if (this.usarTasaAutomatica && this.tasaBCV && this.form.precioUsd > 0) {
        const tasaValues = this.tasaBCV.precio_usd_bs || this.tasaBCV.precio_venta || this.tasaBCV.tasaVenta || this.tasaBCV;
        const tasa = typeof tasaValues === 'object' ? (tasaValues.precio_usd_bs || 40) : tasaValues;
        this.form.precioBs = parseFloat((this.form.precioUsd * tasa).toFixed(2));
      } else if (this.form.precioUsd === 0) {
        this.form.precioBs = 0;
      }
    },
    
    toggleModoPrecio() {
      this.usarTasaAutomatica = !this.usarTasaAutomatica;
      if (this.usarTasaAutomatica && this.tasaBCV) {
        this.calcularPrecioBs();
      }
    },
    
    async cargarServicios() {
      this.cargando = true;
      try {
        const res = await obtenerServicios(this.filtros.estado);
        if (res.success) {
          this.servicios = res.data;
        } else {
          this.toast.error('Ocurrió un error al sincronizar el catálogo');
        }
      } catch (error) {
        this.toast.error('Servidor fuera de línea o error de red');
      } finally {
        this.cargando = false;
      }
    },
    
    abrirModalCrear() {
      this.editando = false;
      this.form = {
        idServicio: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        precioUsd: 0,
        precioBs: 0,
        costoManoObraUsd: 0,
        duracion: 30,
        garantiaDias: 0,
        garantiaDescripcion: '',
        estado: 'activo'
      };
      this.modalVisible = true;
    },
    
    abrirModalEditar(servicio) {
      this.editando = true;
      this.form = { ...servicio };
      this.modalVisible = true;
    },
    
    cerrarModal() {
      this.modalVisible = false;
      this.editando = false;
    },
    
    async guardarServicio() {
      if (!this.form.codigo?.trim() || !this.form.nombre?.trim()) {
        this.toast.warning('Los campos obligatorios están incompletos');
        return;
      }
      
      this.guardando = true;
      try {
        let result;
        if (this.editando) {
          result = await actualizarServicio(this.form.idServicio, this.form);
        } else {
          // Validaciones de unicidad rápidas antes de enviar
          const existe = await verificarServicioPorCodigo(this.form.codigo);
          if (existe.existe) {
            this.toast.error('El código especificado ya está registrado');
            this.guardando = false;
            return;
          }
          result = await crearServicio(this.form);
        }
        
        if (result.success) {
          this.toast.success(this.editando ? 'Registro actualizado con éxito' : 'Nuevo servicio catalogado');
          this.cerrarModal();
          this.cargarServicios();
        } else {
          this.toast.error(result.error || 'Error en la operación');
        }
      } catch (error) {
        this.toast.error('Error crítico al procesar la solicitud');
      } finally {
        this.guardando = false;
      }
    },
    
    async toggleEstado(servicio) {
      const nuevoEstado = servicio.estado === 'activo' ? 'INACTIVO' : 'ACTIVO';
      const confirmed = await this.modal.confirm({
        title: '¿Cambiar Estatus?',
        message: `El servicio "${servicio.nombre}" pasará a estar ${nuevoEstado}.`,
        icon: 'mdi:help-circle',
        iconType: 'warning',
        confirmText: 'Sí, Actualizar'
      });

      if (confirmed) {
        const toggleApi = servicio.estado === 'activo' ? desactivarServicio : activarServicio;
        const result = await toggleApi(servicio.idServicio);
        if (result.success) {
          this.toast.success('Estado actualizado correctamente');
          this.cargarServicios();
        } else {
          this.toast.error('No se pudo cambiar el estado');
        }
      }
    },
    
    async confirmarEliminar(servicio) {
      const confirmed = await this.modal.confirm({
        title: '¿Eliminar Servicio?',
        message: 'Esta acción es irreversible y podría afectar órdenes históricas.',
        icon: 'mdi:alert-circle',
        iconType: 'danger',
        confirmText: 'Sí, Eliminar Permanentemente',
        type: 'danger'
      });

      if (confirmed) {
        const result = await eliminarServicio(servicio.idServicio);
        if (result.success) {
          this.toast.info('Registro eliminado del catálogo');
          this.cargarServicios();
        } else {
          this.toast.error('El registro no puede eliminarse (posibles dependencias)');
        }
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

.transition-all { transition: all 0.3s ease; }
html { scroll-behavior: smooth; }

@media (max-width: 640px) {
  button { min-height: 44px; min-width: 44px; }
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
  transition: all 0.2s;
}

input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  background: #2563eb;
}

input[type="range"]::-moz-range-thumb {
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;  
  line-clamp: 1;
  overflow: hidden;
}
</style>